import { stringify } from "query-string";
import { gunzipSync } from "zlib";
import { instance } from "../instance";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

import {
  CustomerProfileSummary,
  PersistedUser,
  UniqueUser,
  DocumentInput,
  GetUserListRequest,
  GetCognitoUserResponse,
  CognitoTokenResponse,
  CognitoTokenRefreshRequest,
  CognitoTokenRefreshResponse,
} from "../../shared/interfaces";
import { PATH } from "../../shared/constants";

export interface DeleteUserRecordRequest {
  _id: string;
  email: string;
  game: string;
  section: string;
  row: string;
  seat: string;
}

export interface DeleteUsersResponse {
  success: boolean;
  deletedCount: number;
  deletedGroups: number;
}

export interface BackfillCustomerProfilesResponse {
  success: boolean;
  processedCount: number;
  linkedCount: number;
  remainingCount: number;
}

export const getUserListRequest = async (request: GetUserListRequest): Promise<UniqueUser[]> => {
  const result : UniqueUser[] = [];
  const queryStringTemplate = request.games ? (`games=${request.games.join(",")}`) : "";
  let startIndexOfNextResultSet = null;

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const queryString = (startIndexOfNextResultSet == null)
        ? queryStringTemplate
        : `${queryStringTemplate}&startIndex=${startIndexOfNextResultSet}`;

      // eslint-disable-next-line no-await-in-loop
      const response = await instance.get(`${PATH.API}/${PATH.USER}?${queryString}`);

      console.log(`getUserListRequest() - got response of length ${response.data.length}`);
      let partialResultSet : UniqueUser[] = null;
      if (response.headers["content-encoding"] === "gzip") {
        const decompressed = gunzipSync(response.data);
        partialResultSet = JSON.parse(decompressed.toString("utf8"));
      } else {
        partialResultSet = response.data;
      }

      // append the newly retrieved results to the resultset to be returned
      result.push(...partialResultSet);

      const lastUserInResultSet : any = result[result.length - 1];
      if ("unretrievedUsers" in lastUserInResultSet) {
        console.log(`getUserListRequest() - last user contains indicator that there are ${lastUserInResultSet.unretrievedUsers} more users in result set; making another API call to get the remainer...`);
        startIndexOfNextResultSet = result.length;
      } else {
        break;
      }
    }

    return result;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getUserByIdRequest = async (userId: string): Promise<PersistedUser> => {
  try {
    const response = await instance.get(`${PATH.API}/${PATH.USER}/${userId}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addUserRequest = async (request: FormData): Promise<DocumentInput> => {
  try {
    const response = await instance.post<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.USER}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateUserRequest = async (
  request: FormData, userId: string,
): Promise<DocumentInput> => {
  try {
    const response = await instance.put<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.USER}/${userId}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteUserRequest = async (
  request: DeleteUserRecordRequest,
): Promise<DeleteUsersResponse> => {
  try {
    const response = await instance.delete<DeleteUsersResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.USER}`,
      {
        data: {
          records: [request],
        },
      },
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteUsersRequest = async (
  requests: DeleteUserRecordRequest[],
): Promise<DeleteUsersResponse> => {
  try {
    const response = await instance.delete<DeleteUsersResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.USER}`,
      {
        data: {
          records: requests,
        },
      },
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

/** Must match server `PROFILE_LIST_ALL_MOMENTO_TEAMS` in user service. */
export const PROFILES_ALL_MOMENTO_TEAMS_TEAM_ID = "__all_momento_teams__";

export interface ProfileListItem {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  registrationCount: number;
  downloadCount: number;
  shareCount: number;
  imagesViewedCount: number;
  isSeasonTicketHolder: boolean;
  created: string;
  lastActivityAt?: string;
  /** Distinct games this profile has registrations for (operator list). */
  uniqueGamesCount: number;
  gamesList: string[];
  registrationDetails: Array<{ gameLabel: string; registeredAt: string }>;
}

/** Server caps `limit` (see UserService.PROFILE_LIST_MAX_LIMIT, currently 10_000). */
export const PROFILE_LIST_MAX_LIMIT = 10000;

export interface ProfileListResponse {
  profiles: ProfileListItem[];
  totalCount: number;
}

export const listProfilesByTeamRequest = async (params: {
  teamId: string;
  sort?: "most_recent" | "most_downloads" | "most_accessed" | "season_ticket";
  limit?: number;
  staffEmailsOnly?: boolean;
}): Promise<ProfileListResponse> => {
  try {
    const qs = stringify({
      teamId: params.teamId,
      sort: params.sort || "most_recent",
      limit: params.limit || 100,
      ...(params.staffEmailsOnly ? { staffEmailsOnly: "true" } : {}),
    });
    const response = await instance.get<ProfileListResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.USER}/profile/list?${qs}`,
    );
    const data = response.data as ProfileListResponse | ProfileListItem[] | undefined;
    if (Array.isArray(data)) {
      return { profiles: data, totalCount: data.length };
    }
    if (data && Array.isArray((data as ProfileListResponse).profiles)) {
      const body = data as ProfileListResponse;
      return {
        profiles: body.profiles,
        totalCount: typeof body.totalCount === "number" ? body.totalCount : 0,
      };
    }
    return { profiles: [], totalCount: 0 };
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getCustomerProfileRequest = async (
  profileId: string,
): Promise<CustomerProfileSummary> => {
  try {
    const response = await instance.get<CustomerProfileSummary>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.USER}/profile/${profileId}`,
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteProfileRequest = async (
  profileId: string,
): Promise<{ deleted: boolean }> => {
  try {
    const response = await instance.delete<{ deleted: boolean }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.USER}/profile/${profileId}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const backfillCustomerProfilesRequest = async (
  limit = 5000,
): Promise<BackfillCustomerProfilesResponse> => {
  try {
    const response = await instance.post<BackfillCustomerProfilesResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.USER}/profile/backfill`,
      { limit },
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const exchangeCognitoCodeForTokensRequest = async (code?: string, redirect_uri?: string):
  Promise<CognitoTokenResponse> => {
  try {
    let paramsToAppend = "";
    if (code) {
      paramsToAppend = `cognitoCode=${code}`;
    }
    if (redirect_uri) {
      paramsToAppend = `${paramsToAppend}${paramsToAppend ? "&" : ""}redirect_uri=${redirect_uri}`;
    }

    const response: any = await instance.get(`${PATH.API}/${PATH.USER}/cognitoUser/tokens?${paramsToAppend}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const refreshCognitoTokensRequest = async (request: CognitoTokenRefreshRequest):
  Promise<CognitoTokenRefreshResponse> => {
  try {
    const response: any = await instance.put(`${PATH.API}/${PATH.USER}/cognitoUser/tokens`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(JSON.stringify(err));
  }
};

export const getCognitoUserRequest = async (emailAddress?: string, accessToken?: string):
  Promise<GetCognitoUserResponse> => {
  try {
    let paramsToAppend = "";
    if (emailAddress) {
      paramsToAppend = `emailAddress=${emailAddress}`;
    }
    if (accessToken) {
      paramsToAppend += `${(paramsToAppend.length === 0) ? "" : "&"}accessToken=${accessToken}`;
    }

    const config = {
      headers: {
        Authorization: `bearer ${accessToken}`,
      },
    };

    const response: any = await instance.get(`${PATH.API}/${PATH.USER}/cognitoUser?${paramsToAppend}`, config);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
